import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  faBars,
  faExternalLinkSquareAlt,
} from "@fortawesome/free-solid-svg-icons";
import { logError, logLiveKit, LogCategory, LogLevel, setSessionContext, clearSessionContext } from "utils/sentryLogger";
import { updateProfile } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { motion } from "framer-motion";
import { ConnectionState } from "livekit-client";
import { Join } from "./Join/Join";
import { Activity } from "./Activity/Activity";
import { MenuButton } from "./MenuButton";
import { Dashboard } from "./Dashboard/Dashboard";
import { TTSParticipant } from "./TTSParticipant";
import { LiveKitProgrammableVideoProvider } from "hooks/livekit/useLiveKitProgramableVideo/useLiveKitProgramableVideo";
import useConnectionOptions from "hooks/livekit/useConnectionOptions/useConnectionOptions";
import useRoomOptions from "hooks/livekit/useRoomOptions/useRoomOptions";
import { getRef, SessionProvider, useSessionContext } from "hooks/useSession";
import useRoomState from "hooks/livekit/useRoomState/useRoomState";
import { GestureStateProvider } from "hooks/useGestureState/useGestureState";
import { VolumeProvider } from "hooks/useVolumeContext/useVolumeContext";
import { useAdminPermission } from "hooks/useAdminPermission/useAdminPermission";
import DeviceSettingsModal from "./DeviceSettingsModal/DeviceSettingsModal";
import useAudioPlayback from "hooks/livekit/useAudioPlayback/useAudioPlayback";
import useAudioSessionRecovery from "hooks/livekit/useAudioSessionRecovery/useAudioSessionRecovery";
import { useWakeLock } from "hooks/useWakeLock/useWakeLock";
import { SessionFullModal } from "./SessionFullModal";
import { OrchestratorTimeoutModal } from "./OrchestratorTimeoutModal";
import { OkoSessionProvider } from "hooks/useOkoSession/useOkoSession";
import { useAppStateContext } from "hooks/useAppState/useAppState";
import { request } from "hooks/useOkoApi/useOkoApi";
import { auth, firestore } from "hooks/useFirebase";
import { useAuthUserDataContext } from "hooks/useAuthUserData/useAuthUserData";
import { useClientInfo } from "hooks/useClientInfo";
import { useUser } from "hooks/useUser/useUser";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";
import { constants, logSession } from "utils";
import { VideosBar } from "./shared/VideosBar/VideosBar";
import useDevices from "hooks/livekit/useDevices/useDevices";
import useDeviceReadiness from "hooks/livekit/useDeviceReadiness/useDeviceReadiness";
import { useOrchestratorHeartbeat } from "hooks/useOrchestratorHeartbeat/useOrchestratorHeartbeat";
import { setupUserProfile, joinAndFetchSession, checkSessionStatus, getNetworkInfo, redirectToGroupCode, ONBOARDING_STEP, ONBOARDING_SCREEN, REDIRECT_ERROR, buildSessionDocData, ensureSessionDocExists } from "./sessionPrefetch";
import { shouldPrefetch } from "./useSessionPrefetchReady";
import { ReconnectingOverlay } from "./ReconnectingOverlay";
import { ErrorBoundary } from "components/shared/ErrorBoundary/ErrorBoundary";
import { saveSessionState, clearSessionState, SESSION_PHASE } from "utils/sessionPersistence";
import { useSessionRehydration } from "hooks/useSessionRehydration/useSessionRehydration";
import { IMPERSONATION_BLOCK_MESSAGE, IMPERSONATION_ERROR_CODE, isActingAsAnotherTeacher } from "./impersonationBlock";

// Renderless: mounts the blocked-audio recovery (any tap while iOS has blocked
// playback calls room.startAudio()) and the interrupted-audio-session recovery
// for every session surface.
const AudioPlaybackRecovery = () => {
  useAudioPlayback();
  useAudioSessionRecovery();
  return null;
};

// Renderless: keeps the iPad from dimming/locking across every session surface.
const ScreenWakeLock = () => {
  useWakeLock();
  return null;
};

export const Session = () => {
  const connectionOptions = useConnectionOptions();
  const roomOptions = useRoomOptions();
  const { isSpectator, sessionAuthType } = useAppStateContext();

  const onError = (error) => {
    // AudioContext closed or GC'd while LiveKit was setting up audio processing
    if (error.name === "InvalidStateError" && error.message?.includes("AudioWorkletNode")) {
      logLiveKit("AudioWorkletNode creation failed — AudioContext no longer available", {
        level: LogLevel.WARNING,
        event: "audio_worklet_invalid_state",
        errorName: error.name,
        errorMessage: error.message,
      });
      return;
    }

    logLiveKit("Unhandled LiveKit error", {
      level: LogLevel.WARNING,
      event: "livekit_unhandled_error",
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack,
    });
  };

  return (
    <SessionProvider>
      {(sessionAuthType || isSpectator) && <LiveKitProgrammableVideoProvider
        connectionOptions={connectionOptions}
        roomOptions={roomOptions}
        onError={onError}
      >
        <OkoSessionProvider>
          <DeviceSettingsModal />
          <AudioPlaybackRecovery />
          <ScreenWakeLock />
          <GestureStateProvider>
            <VolumeProvider>
              <Room />
            </VolumeProvider>
          </GestureStateProvider>
        </OkoSessionProvider>
      </LiveKitProgrammableVideoProvider>}
    </SessionProvider>
  );
};

const Room = () => {
  const roomState = useRoomState();
  const { hasAdminPermission } = useAdminPermission();
  const [isLoading, setIsLoading] = useState(false);
  const { displayName, groupCode, sessionAuthType, isSpectator } = useAppStateContext();
  const isDemoMode = new URLSearchParams(window.location.search).get("demo") === "true";
  const { isStart, setIsStart, isStartObserving, setIsStartObserving, setSessionId, setGroupCode: setSessionGroupCode, setUserRole, setAuthMethod, setLocalSessionActivityType, setWaitingRoomInit, currentEventState } = useSessionContext();
  const { authUserData, setOkoUserId } = useAuthUserDataContext();
  const { update: updateUser } = useUser(authUserData?.id);
  const { room, connect: connectVideo } = useVideoContext();
  const { audioInputDevices, audioOutputDevices } = useDevices();
  const { devicesReady } = useDeviceReadiness({ audioInputDevices, audioOutputDevices });
  const [error, setError] = useState();
  const [showSessionFullModal, setShowSessionFullModal] = useState(false);
  const [isWaitingForSessionToStart, setIsWaitingForSessionToStart] = useState(false);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [prefetchedResponse, setPrefetchedResponse] = useState(null);
  const clientInfo = useClientInfo();
  const deviceCheckStartTime = useRef(null);
  const currentSessionIdRef = useRef(null);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isAutoReconnecting, setIsAutoReconnecting] = useState(false);
  const autoReconnectAttempts = useRef(0);
  const resetHeartbeatRef = useRef(null);
  const MAX_AUTO_RECONNECT_ATTEMPTS = 2;

  // Clear the shared session envelope when this screen unmounts so logs
  // emitted from other parts of the app afterwards don't carry stale fields.
  useEffect(() => () => clearSessionContext(), []);

  const ensureFirestoreSessionDoc = useCallback(async (groupResponse, sessionResponse) => {
    const ref = getRef(groupResponse.session_id);
    const docRef = doc(firestore, ref.path);
    const data = buildSessionDocData({ groupResponse, sessionResponse, serverTimestamp });
    await ensureSessionDocExists({ docRef, data, getDoc, setDoc });
  }, []);

  const handleAutoReconnect = useCallback(async () => {
    setIsAutoReconnecting(true);
    try {
      room.disconnect();

      const displayNameToSet = displayName || authUserData?.displayName;
      const { groupResponse, sessionResponse } = await joinAndFetchSession({
        groupCode,
        displayName: displayNameToSet,
        clientInfo,
        authUserData,
      });

      if (groupResponse?.user_id != null) setOkoUserId(groupResponse.user_id);
      setSessionContext({
        sessionId: groupResponse.session_id,
        groupCode,
        activityType: sessionResponse.activity_type,
        sessionStatus: sessionResponse.status,
        livekitUrl: groupResponse.livekit_url,
      });

      setSessionId(groupResponse.session_id);
      setSessionGroupCode(groupCode);
      setLocalSessionActivityType(sessionResponse.activity_type);
      currentSessionIdRef.current = groupResponse.session_id;

      await connectVideo(groupResponse.livekit_url, groupResponse.livekit_auth_token, {
        sessionId: groupResponse.session_id,
        groupCode,
        userId: authUserData?.id,
      });

      await ensureFirestoreSessionDoc(groupResponse, sessionResponse);

      resetHeartbeatRef.current?.();
      setIsAutoReconnecting(false);
    } catch (e) {
      logError("Auto-reconnect failed", e, {
        category: LogCategory.SESSION,
        groupCode,
        userId: authUserData?.id,
        attempt: autoReconnectAttempts.current,
      });
      setIsAutoReconnecting(false);
      setShowTimeoutModal(true);
    }
  }, [room, groupCode, displayName, authUserData, clientInfo, connectVideo, setSessionId, setSessionGroupCode, setLocalSessionActivityType, ensureFirestoreSessionDoc]);

  const handleTimeout = useCallback(async (timeoutInfo) => {
    const currentSessionId = currentSessionIdRef.current || prefetchedResponse?.groupResponse?.session_id;
    logError("Orchestrator heartbeat timeout — session may be dead", null, {
      category: LogCategory.SESSION,
      groupCode,
      sessionId: currentSessionId,
      userId: authUserData?.id,
      sessionAuthType,
      // Distinguishes wake-up-adjacent timeouts (device recently returned from
      // background) from steady-state orchestrator silence.
      secondsSinceResume: timeoutInfo?.secondsSinceResume ?? null,
    });

    if (!currentSessionId) {
      setShowTimeoutModal(true);
      return;
    }

    try {
      const result = await request(`session/${currentSessionId}/report_crash`, { method: "POST" });
      if (result.orchestrator_alive && autoReconnectAttempts.current < MAX_AUTO_RECONNECT_ATTEMPTS) {
        autoReconnectAttempts.current += 1;
        handleAutoReconnect();
        return;
      }
    } catch {
      // API call failed — fall through to crash modal
    }

    setShowTimeoutModal(true);
  }, [groupCode, authUserData?.id, sessionAuthType, prefetchedResponse, handleAutoReconnect]);

  const onHeartbeatReceived = useCallback(() => {
    autoReconnectAttempts.current = 0;
  }, []);

  const { resetHeartbeat } = useOrchestratorHeartbeat({
    room,
    enabled: !isRestarting && !isAutoReconnecting && !showTimeoutModal && currentEventState !== "complete",
    onTimeout: handleTimeout,
    onHeartbeatReceived,
    timeoutSeconds: 30,
  });

  useEffect(() => {
    resetHeartbeatRef.current = resetHeartbeat;
  }, [resetHeartbeat]);

  const { isRehydrating } = useSessionRehydration({
    authUserData,
    isStart,
    isSessionReady,
    prefetchedResponse,
    setIsStart,
    currentEventState,
  });

  const handleSessionRestart = async () => {
    setIsRestarting(true);
    setShowTimeoutModal(false);

    try {
      room.disconnect();

      const displayNameToSet = displayName || authUserData?.displayName;
      const { groupResponse, sessionResponse } = await joinAndFetchSession({
        groupCode,
        displayName: displayNameToSet,
        clientInfo,
        authUserData,
      });

      if (groupResponse?.user_id != null) setOkoUserId(groupResponse.user_id);
      setSessionContext({
        sessionId: groupResponse.session_id,
        groupCode,
        activityType: sessionResponse.activity_type,
        sessionStatus: sessionResponse.status,
        livekitUrl: groupResponse.livekit_url,
      });

      setSessionId(groupResponse.session_id);
      setSessionGroupCode(groupCode);
      setLocalSessionActivityType(sessionResponse.activity_type);
      currentSessionIdRef.current = groupResponse.session_id;

      await connectVideo(groupResponse.livekit_url, groupResponse.livekit_auth_token, {
        sessionId: groupResponse.session_id,
        groupCode,
        userId: authUserData?.id,
      });

      await ensureFirestoreSessionDoc(groupResponse, sessionResponse);

      resetHeartbeat();
      setIsRestarting(false);
    } catch (e) {
      logError("Failed to restart session after orchestrator timeout", e, {
        category: LogCategory.SESSION,
        groupCode,
        userId: authUserData?.id,
        errorCode: e?.detail?.error_code,
      });
      redirectToGroupCode(groupCode, REDIRECT_ERROR.RETRY_FAILURE, {
        source: "orchestrator_timeout_restart",
        sessionId: currentSessionIdRef.current,
        statusCode: e?.status,
      });
    }
  };

  // One sentence for both ways a user can be stopped: the client-side guard
  // below and the server's IMPERSONATION_ACTIVE refusal.
  const showImpersonationBlock = (stopEntering) => {
    setError(IMPERSONATION_BLOCK_MESSAGE);
    setIsLoading(false);
    stopEntering(false);
  };

  // Entering a live session while acting as another teacher is refused here,
  // before any request goes out, because /group/{code}/join and
  // /group/{code}/observe are not refused by the server.
  const refuseEntryWhileImpersonating = (stopEntering) => {
    if (!isActingAsAnotherTeacher()) return false;
    showImpersonationBlock(stopEntering);
    return true;
  };

  // Pre-fetch session data on page load (join API + session GET + Firestore) - but NOT LiveKit
  const prefetchSessionData = async () => {
    if (refuseEntryWhileImpersonating(setIsStart)) return;
    return Sentry.startSpan(
      {
        name: "session.prefetch",
        op: "session.setup",
        attributes: {
          "session.group_code": groupCode || "",
          "user.id": authUserData?.id || "",
        },
      },
      async (span) => {
        const startTime = Date.now();

        try {
          // Steps 1 & 2: Set up user profile (non-blocking — errors logged internally)
          const { displayNameToSet, role } = await setupUserProfile({
            sessionAuthType,
            currentUser: auth.currentUser,
            displayName,
            authUserData,
            groupCode,
            updateUser,
          });

          // Steps 3 & 4: Join API + session metadata (step-level logging inside)
          const { groupResponse, sessionResponse } = await joinAndFetchSession({
            groupCode,
            displayName: displayNameToSet,
            clientInfo,
            authUserData,
          });

          if (groupResponse?.user_id != null) setOkoUserId(groupResponse.user_id);
          setSessionContext({
            sessionId: groupResponse.session_id,
            groupCode,
            activityType: sessionResponse.activity_type,
            sessionStatus: sessionResponse.status,
            livekitUrl: groupResponse.livekit_url,
          });

          // Log overall prefetch duration
          const setupDuration = Date.now() - startTime;
          logSession("Session prefetch completed", {
            setupDurationMs: setupDuration,
            sessionId: groupResponse.session_id,
            groupCode,
            userId: authUserData?.id,
            okoUserId: undefined,
            onboardingStep: ONBOARDING_STEP.SESSION_JOIN,
            screen: ONBOARDING_SCREEN.DEVICE_CHECK,
            ...getNetworkInfo(),
          });

          // Step 5: Store responses for use when user clicks Join
          // Note: Don't set session context here - that would trigger Firestore listener and hide camera preview
          setPrefetchedResponse({ groupResponse, sessionResponse, role, displayNameToSet });

          // Mark device check screen start time (user now sees mic/camera preview)
          deviceCheckStartTime.current = Date.now();

          setIsSessionReady(true);
        } catch (e) {
          span.setStatus({ code: 2, message: e?.message || "prefetch_failed" });
          console.error(e);
          if (e?.detail?.error_code === "SESSION_FULL") {
            logError("Session is full", e, {
              category: LogCategory.SESSION,
              groupCode,
              userId: authUserData?.id,
              okoUserId: undefined,
              onboardingStep: ONBOARDING_STEP.SESSION_JOIN,
              screen: ONBOARDING_SCREEN.DEVICE_CHECK,
            });
            setShowSessionFullModal(true);
            return;
          }
          if (e?.detail?.error_code === "SESSION_CLOSED") {
            logError("Session closed during prefetch", e, {
              category: LogCategory.SESSION,
              groupCode,
              userId: authUserData?.id,
              okoUserId: undefined,
              onboardingStep: ONBOARDING_STEP.SESSION_JOIN,
              screen: ONBOARDING_SCREEN.DEVICE_CHECK,
            });
            redirectToGroupCode(groupCode, REDIRECT_ERROR.SESSION_NOT_FOUND, {
              source: ONBOARDING_STEP.SESSION_JOIN,
              statusCode: e?.status,
            });
            return;
          }
          if (e?.detail?.error_code === IMPERSONATION_ERROR_CODE) {
            showImpersonationBlock(setIsStart);
            return;
          }
          logError("Failed to prefetch session", e, {
            category: LogCategory.SESSION,
            groupCode,
            userId: authUserData?.id,
            okoUserId: undefined,
            sessionAuthType,
            statusCode: e?.status,
            onboardingStep: ONBOARDING_STEP.SESSION_JOIN,
            screen: ONBOARDING_SCREEN.DEVICE_CHECK,
          });
          redirectToGroupCode(groupCode, REDIRECT_ERROR.RETRY_FAILURE, {
            source: ONBOARDING_STEP.SESSION_JOIN,
            statusCode: e?.status,
          });
        }
      }
    );
  };

  // Connect to LiveKit and finalize session when user clicks Join
  const connectAndFinalize = async () => {
    return Sentry.startSpan(
      {
        name: "session.connect",
        op: "session.connect",
        attributes: {
          "session.group_code": groupCode || "",
          "session.id": prefetchedResponse?.groupResponse?.session_id || "",
          "user.id": authUserData?.id || "",
        },
      },
      async (span) => {
        const connectStartTime = Date.now();

        try {
          const { groupResponse, sessionResponse, role } = prefetchedResponse;

          // Re-check session status before connecting — the session may have
          // been closed/ended between prefetch and the user clicking Join/I'm Ready.
          const { isActive, redirectError: statusRedirectError } = await checkSessionStatus({
            sessionId: groupResponse.session_id,
            groupCode,
            userId: authUserData?.id,
            source: "device_check_join",
          });
          if (!isActive) {
            redirectToGroupCode(groupCode, statusRedirectError, {
              source: "device_check_join",
              sessionId: groupResponse.session_id,
            });
            return;
          }

          // Log device check duration (time user spent on mic/camera preview before clicking Join)
          const deviceCheckDurationMs = deviceCheckStartTime.current
            ? Date.now() - deviceCheckStartTime.current
            : null;
          logSession("Device check completed", {
            sessionId: groupResponse.session_id,
            groupCode,
            userId: authUserData?.id,
            okoUserId: undefined,
            deviceCheckDurationMs,
            onboardingStep: ONBOARDING_STEP.DEVICE_CHECK,
            screen: ONBOARDING_SCREEN.DEVICE_CHECK,
            ...getNetworkInfo(),
          });

          // Log when user initiates connection
          logSession("User joined session", {
            sessionId: groupResponse.session_id,
            groupCode,
            userId: authUserData?.id,
            // Stable identifiers instead of the student's name (PII): firebaseUid
            // is always present; okoUserId is null for anonymous users.
            firebaseUid: auth.currentUser?.uid ?? null,
            okoUserId: groupResponse?.user_id ?? null,
            role,
            authMethod: sessionAuthType,
            onboardingStep: ONBOARDING_STEP.CONNECT,
            screen: ONBOARDING_SCREEN.DEVICE_CHECK,
          });

          // Persist session context
          console.log("[oko-patter] Session init — groupCode:", groupCode, "sessionId:", groupResponse.session_id, "livekitUrl:", groupResponse.livekit_url);
          setSessionId(groupResponse.session_id);
          setSessionGroupCode(groupCode);
          setUserRole(role);
          setAuthMethod(sessionAuthType);
          setLocalSessionActivityType(sessionResponse.activity_type);
          if (sessionResponse.waiting_room) {
            setWaitingRoomInit(sessionResponse.waiting_room);
          }

          // Save session ID to sessionStorage before connecting
          saveSessionState({ sessionId: groupResponse.session_id, userRole: role });

          // 1. Connect to LiveKit
          await connectVideo(groupResponse.livekit_url, groupResponse.livekit_auth_token, {
            sessionId: groupResponse.session_id,
            groupCode,
            userId: authUserData?.id,
          });

          // 2. Create the Firestore session document only if it doesn't exist
          // yet, so a late-joining student can't reset activityId to "ready-room"
          // on a session already in progress. (See ensureSessionDocExists.)
          await ensureFirestoreSessionDoc(groupResponse, sessionResponse);

          // Mark session as fully active in sessionStorage
          saveSessionState({ phase: SESSION_PHASE.ACTIVE });

          // Log successful connection with duration
          const connectDurationMs = Date.now() - connectStartTime;
          logSession("Connect completed", {
            sessionId: groupResponse.session_id,
            groupCode,
            userId: authUserData?.id,
            okoUserId: undefined,
            connectDurationMs,
            onboardingStep: ONBOARDING_STEP.CONNECT,
            screen: ONBOARDING_SCREEN.DEVICE_CHECK,
          });
        } catch (e) {
          span.setStatus({ code: 2, message: e?.message || "connect_failed" });
          console.error(e);
          const connectDurationMs = Date.now() - connectStartTime;
          setError(e?.detail?.message || e?.detail || e?.message || "Failed to connect to video.");
          setIsLoading(false);
          setIsStart(false);
          logError("Failed to connect to LiveKit", e, {
            category: LogCategory.SESSION,
            groupCode,
            userId: authUserData?.id,
            okoUserId: undefined,
            sessionAuthType,
            connectDurationMs,
            onboardingStep: ONBOARDING_STEP.CONNECT,
            screen: ONBOARDING_SCREEN.DEVICE_CHECK,
          });
        }
      }
    );
  };

  // Fallback for edge cases
  // eslint-disable-next-line no-unused-vars
  const startSession = async () => {
    if (refuseEntryWhileImpersonating(setIsStart)) return;
    try {
      const currentUser = auth.currentUser;
      if (sessionAuthType === constants.ANONYMOUS && currentUser.isAnonymous) {
        updateProfile(currentUser, { displayName: displayName })
          .catch((e) => {
            setIsLoading(false);
            logError("Failed to update Firebase displayName", e, {
              category: LogCategory.AUTH,
              sessionAuthType,
              groupCode,
            });
            console.log(e);
          });
      }
      const role = constants.STUDENT;
      const displayNameToSet = displayName || authUserData.displayName;
      await updateUser({ displayNameToSet, role });
      const groupResponse = await request(`group/${groupCode}/join/?display_name=${displayNameToSet}`,
          { method: "POST", payload: clientInfo }
      );

      // Log when user joins a session
      logSession("User joined session", {
        sessionId: groupResponse.session_id,
        groupCode,
        userId: authUserData?.id,
        // Stable identifiers instead of the student's name (PII): firebaseUid is
        // always present; okoUserId is null for anonymous users.
        firebaseUid: auth.currentUser?.uid ?? null,
        okoUserId: groupResponse?.user_id ?? null,
        role,
        authMethod: sessionAuthType,
      });

      // Persist session context for use in other components (e.g., ReadyRoom)
      setSessionId(groupResponse.session_id);
      setSessionGroupCode(groupCode);
      setUserRole(role);
      setAuthMethod(sessionAuthType);

      await connectVideo(groupResponse.livekit_url, groupResponse.livekit_auth_token, {
        sessionId: groupResponse.session_id,
        groupCode,
        userId: authUserData?.id,
      });

      const sessionResponse = await request(`session/?session_id=${groupResponse.session_id}`, { method: "GET" });
      setLocalSessionActivityType(sessionResponse.activity_type);
      if (sessionResponse.waiting_room) {
        setWaitingRoomInit(sessionResponse.waiting_room);
      }
      await ensureFirestoreSessionDoc(groupResponse, sessionResponse);

    } catch (e) {
      console.error(e);
      if (e?.detail?.error_code === "SESSION_FULL") {
        setShowSessionFullModal(true);
        setIsLoading(false);
        setIsStart(false);
        return;
      }
      if (e?.detail?.error_code === "SESSION_CLOSED") {
        redirectToGroupCode(groupCode, undefined, {
          source: "start_session",
          sessionId: currentSessionIdRef.current,
        });
        return;
      }
      if (e?.detail?.error_code === IMPERSONATION_ERROR_CODE) {
        showImpersonationBlock(setIsStart);
        return;
      }
      setError(e?.detail?.message || e?.detail || e?.message || "An unknown error occurred.");
      setIsLoading(false);
      setIsStart(false);
      logError("Failed to start session", e, {
        category: LogCategory.SESSION,
        groupCode,
        userId: authUserData?.id,
        sessionAuthType,
      });
    }
  };

  const startObserving = async () => {
    if (refuseEntryWhileImpersonating(setIsStartObserving)) return;
    let isSessionStarted = false;
    let i = 0;
    let isNoRetry = false;
    while (!isSessionStarted && i < 40) { // 10 minutes timeout with 15 second reconnect interval.
      await request(`group/${groupCode}/observe`, { method: "GET" })
        // eslint-disable-next-line no-loop-func
        .then(async (response) => {
          isSessionStarted = true;
          await connectVideo(response.livekit_url, response.livekit_auth_token, {
            sessionId: response.session_id,
            groupCode,
            userId: authUserData?.id,
          });
        })
        // eslint-disable-next-line no-loop-func
        .catch(async (e) => {
          if (e?.detail?.includes("Access denied")) {
            setError(e.detail);
            setIsLoading(false);
            setIsStartObserving(false);
            isNoRetry = true;
          }
          setIsWaitingForSessionToStart(true);
          if (i >= 39) {
            setError(e?.detail || e?.message || "An unknown error occurred.");
            setIsLoading(false);
            setIsStartObserving(false);
          }
          i++;
          // Wait 15 seconds before trying to reconnect.
          await new Promise(resolve => setTimeout(resolve, 15000));
        });
      if (isNoRetry) {
        break;
      }
    }
  };

  // Pre-fetch session data only after a working mic is confirmed (devicesReady)
  useEffect(() => {
    const autoPrefetch = async () => {
      const displayNameToSet = displayName || authUserData?.displayName;
      if (shouldPrefetch({ isSpectator, groupCode, displayName: displayNameToSet, devicesReady, isSessionReady, error, prefetchedResponse, isDemoMode })) {
        await prefetchSessionData();
      }
    };
    autoPrefetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupCode, displayName, authUserData?.displayName, isSpectator, devicesReady, isSessionReady, error, prefetchedResponse]);

  // Handle "Join" button click - connect to LiveKit and transition
  useEffect(() => {
    const handleJoinClick = async () => {
      if (!error && isStart && !isSpectator && isSessionReady && prefetchedResponse) {
        // Connect to LiveKit and create Firestore doc using prefetched response
        setIsLoading(true);
        await connectAndFinalize();
        // Add 2-second delay after successful connection before transitioning
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Log total time user spent on device check screen before transitioning to activity
        const totalDeviceCheckScreenMs = deviceCheckStartTime.current
          ? Date.now() - deviceCheckStartTime.current
          : null;
        logSession("Leaving device check screen", {
          sessionId: prefetchedResponse.groupResponse?.session_id,
          groupCode,
          userId: authUserData?.id,
          totalDeviceCheckScreenMs,
          screen: ONBOARDING_SCREEN.DEVICE_CHECK,
        });

        setIsLoading(false);
        // UI will transition via roomState check after LiveKit connects
      } else if (!error && !isLoading && isSpectator && isStartObserving) {
        // Spectator flow remains unchanged
        setIsLoading(true);
        await startObserving();
      }
    };
    handleJoinClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isStart, isStartObserving, isSpectator, isSessionReady, prefetchedResponse]);

  // Show Join screen if: not connected yet OR user hasn't clicked Join yet
  // Skip during restart to avoid flashing the Join screen while reconnecting
  if ((roomState === ConnectionState.Disconnected || !isStart) && !isRestarting && !isAutoReconnecting && !isRehydrating) return (
    <>
      <SessionFullModal
        open={showSessionFullModal}
        onClose={() => {
          setShowSessionFullModal(false);
          clearSessionState();
          window.location.href = "/";
        }}
      />
      <Join error={error}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setError={setError}
            isSpectator={isSpectator}
            isWaitingForSessionToStart={isWaitingForSessionToStart}
            isSessionReady={isSessionReady}/>
    </>
  );

  return (
    <>
      {(isAutoReconnecting || isRehydrating) && <ReconnectingOverlay />}
      <OrchestratorTimeoutModal
        open={showTimeoutModal}
        onRestart={handleSessionRestart}
        isRestarting={isRestarting}
      />
      <TTSParticipant />
      {isSpectator ? <VideosBar isSpectator /> : hasAdminPermission ? <AdminUi /> : (
        <ErrorBoundary>
          <Activity />
        </ErrorBoundary>
      )}
    </>
  );
};

const AdminUi = () => {
  const [showDashboard, setShowDashboard] = useState(false);

  return (
    <>
      <MenuButton
        style={{ position: "absolute", left: 16, top: 16 }}
        icon={showDashboard ? faExternalLinkSquareAlt : faBars}
        ariaLabel={showDashboard ? "Close dashboard" : "Open dashboard"}
        onClick={() => {
          setShowDashboard(!showDashboard);
        }}
      />
      {showDashboard && <Dashboard />}
      <motion.div
        style={{
          transformOrigin: "top left",
          cursor: showDashboard ? "pointer" : "auto",
          boxShadow: "transparent 1px 1px 1px 1px",
        }}
        animate={{
          scale: showDashboard ? (260 - 20) / window.innerWidth : 1,
          x: showDashboard ? 10 : 0,
          y: showDashboard ? 10 : 0,
        }}
        transition={{ type: "linear" }}
        onClick={() => {
          showDashboard && setShowDashboard(!showDashboard);
        }}
      >
        <Activity disable={showDashboard} />
      </motion.div>
    </>
  );
};
